











































import Vue from 'vue';
import _ from 'lodash';
import { sanityClient } from '@/config/sanity';
import Customer from '@/models/customer';
import Story, { StoryType } from '@/models/story';
import Quote from '@/models/quote';
import Department, { stringToDepartment } from '@/models/department';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import CustomersPanel from '@/components/CustomersPanel.vue';
import StoriesPanel from '@/components/StoriesPanel.vue';
import SpeakerWithGradient from '@/components/SpeakerWithGradient.vue';
import {
  parseDateTimeFromSanityAutoGeneratedTimestamp,
  formatToLongDisplayFormat,
} from '@/utils/date';
import SanityVideoPublication from '@/models/sanitySchema/sanityVideoPublication';
import SplashPanel from './SplashPanel.vue';
import SummaryPanel from './SummaryPanel.vue';
import DepartmentsPanel from './DepartmentsPanel.vue';
import ReadMorePanel from './ReadMorePanel.vue';

interface HomePage {
  splashPanel: {
    text: string;
  };
  summaryPanel: {
    text: string;
  };
  departmentsPanel: {
    mediaButtonText: string;
    mediaText: string;
    techButtonText: string;
    techText: string;
  };
  storiesPanel: {
    headline: string;
  };
  customersPanel: {
    headline: string;
  };
}

interface Data {
  isLoading: boolean;
  error: string;
  homePage: HomePage | null;
  stories: Array<Story>;
  customers: Array<Customer>;
  quotes: Array<Quote>;
  videoPublicationStories: Array<Story>;
}

export default Vue.extend({
  name: 'Home',
  components: {
    LoadingIndicator,
    ErrorMessage,
    SplashPanel,
    SummaryPanel,
    DepartmentsPanel,
    StoriesPanel,
    CustomersPanel,
    ReadMorePanel,
    SpeakerWithGradient,
  },
  data(): Data {
    return {
      isLoading: true,
      error: '',
      homePage: null,
      stories: [],
      customers: [],
      quotes: [],
      videoPublicationStories: [],
    };
  },
  created() {
    this.fetchHomePage();
  },
  methods: {
    fetchHomePageFields() {
      const query = `*[_type == "homePage"]{
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        splashText,
        summaryText,
        mediaButtonText,
        mediaDepartmentText,
        techButtonText,
        techDepartmentText,
        storiesPanelHeadline,
        customersPanelHeadline
      }`;

      return sanityClient.fetch(query).then(
        (results) => {
          const page = results[0];
          this.homePage = {
            splashPanel: {
              text: page.splashText,
            },
            summaryPanel: {
              text: page.summaryText,
            },
            departmentsPanel: {
              mediaButtonText: page.mediaButtonText,
              mediaText: page.mediaDepartmentText,
              techButtonText: page.techButtonText,
              techText: page.techDepartmentText,
            },
            storiesPanel: {
              headline: page.storiesPanelHeadline,
            },
            customersPanel: {
              headline: page.customersPanelHeadline,
            },
          };
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchHomePageStories() {
      const query = `*[_type == "story" && frontPage == true] | order(_createdAt desc) {
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        title,
        excerpt,
        image,
        author,
        department,
        publishedDate,
        updatedDate
      }[0...8]`;

      return sanityClient.fetch(query).then(
        (results): Array<Story> => {
          const stories = results.map((r: any) => {
            const createdAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.createdAt,
            );
            const createdAtFormatted = formatToLongDisplayFormat(createdAt);
            const updatedAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.updatedAt,
            );
            const updatedAtFormatted = formatToLongDisplayFormat(updatedAt);
            return {
              ...r,
              createdAt: createdAt.toDate(),
              createdAtFormatted,
              updatedAt: updatedAt.toDate(),
              updatedAtFormatted,
              storyType: StoryType.Story,
            };
          });
          this.stories = stories;
          return stories;
        },
        (error) => {
          this.error = error;
          return [];
        },
      );
    },
    fetchCustomers() {
      const query = '*[_type == "customer"]{ _id, name, logo, quotes }';
      return sanityClient.fetch(query).then(
        (results) => {
          this.customers = results;

          const allQuotes = results.reduce((all: Array<Record<string, string>>, cust: Customer) => {
            if (cust.quotes) {
              return [
                ...all,
                ...cust.quotes.map((q, i) => ({ id: `${cust.name}_quote_${i}`, company: cust.name, quote: q })),
              ];
            }
            return all;
          }, []);

          if (allQuotes.length < 3) {
            this.quotes = allQuotes;
          } else {
            // TODO: Pick 3 random quotes
            this.quotes.push(allQuotes[0]);
            this.quotes.push(allQuotes[1]);
            this.quotes.push(allQuotes[2]);
          }
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchVideoPublicationStories() {
      const query = `*[_type == "videoPublication" && frontPage == true] | order(order) {
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        title,
        author->{name, image, department, title},
        image,
        status,
        vimeoUrl,
        customer->{name},
        description,
        apparatInvolvement,
        articleHeadline,
        article,
        excerpt,
        credits,
        relatedVideos,
        order
      }[0...8]`;

      return sanityClient.fetch(query).then(
        (sanityVideoPublications) => {
          const videos = sanityVideoPublications.map((r: SanityVideoPublication) => {
            const createdAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.createdAt,
            );
            const createdAtFormatted = formatToLongDisplayFormat(createdAt);
            const updatedAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.updatedAt,
            );
            const updatedAtFormatted = formatToLongDisplayFormat(updatedAt);
            return {
              ...r,
              createdAt,
              createdAtFormatted,
              updatedAt,
              updatedAtFormatted,
            };
          });
          const stories: Array<Story> = videos.map((v: SanityVideoPublication) => {
            const vidCreatedAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              v.createdAt,
            );
            const vidUpdatedAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              v.updatedAt,
            );
            return {
              id: v.id,
              key: v.id,
              storyType: StoryType.VideoPublication,
              createdAt: vidCreatedAt.toDate(),
              updatedAt: vidUpdatedAt.toDate(),
              title: v.articleHeadline,
              frontPage: v.frontPage,
              slug: v.slug,
              excerpt: v.excerpt,
              content: v.article,
              department: Department.Media,
              image: v.image,
              author: {
                name: v.author.name,
                slug: v.author.slug,
                image: v.author.image,
                title: v.author.title,
                department: stringToDepartment(v.author.department),
                email: '',
                phone: '',
                location: '',
                website: '',
                linkedIn: '',
                gitHub: '',
                skills: [],
                languages: [],
                hobbies: [],
                about: {},
                hasCV: false,
                order: 0,
              },
              publishedDate: vidCreatedAt.toDate(),
              updatedDate: vidUpdatedAt.toDate(),
              facebookQuote: v.title,
              facebookHashtag: '',
              twitterHashtags: [],
              twitterVia: '',
              otherWebsiteUrl: '',
              otherWebsiteTitle: '',
              emailSubject: '',
              emailBody: '',
            };
          });
          this.videoPublicationStories = stories;
          return stories;
        },
        (error) => {
          this.error = error;
          return [];
        },
      );
    },
    fetchHomePage() {
      this.isLoading = true;
      Promise.all([
        this.fetchHomePageFields(),
        this.fetchHomePageStories(),
        this.fetchVideoPublicationStories(),
        this.fetchCustomers(),
      ]).then((data) => {
        const stories: Array<Story> = data[1];
        const videoPublicationStories: Array<Story> = data[2];
        const allStories = [...stories, ...videoPublicationStories];
        const orderedStories = _.orderBy(allStories, 'createdAt', 'desc');
        const topStories = _.take(orderedStories, 8);
        this.stories = topStories;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    },
  },
});
